import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ClipboardCheck, CheckCircle, XCircle, AlertTriangle, Camera, Upload, User as UserIcon, Package, Truck as TruckIcon } from 'lucide-react';
import { SmartDropdown, FilterSection, useToast, LoadingState, EmptyState } from '../components/ui';
import api from '../services/api';

const QCInspection = () => {
  const queryClient = useQueryClient();
  const { success, error: showError } = useToast();

  const [activeTab, setActiveTab] = useState('pending'); // 'pending' or 'history'
  const [selectedInspection, setSelectedInspection] = useState(null);
  const [inspectionForm, setInspectionForm] = useState({
    visualCheck: '',
    dimensionalCheck: '',
    weightCheck: '',
    packagingCheck: '',
    documentCheck: '',
    notes: '',
    result: '',
    defectTypes: [], // For multi-select when result is FAILED
    sampledQuantity: '',
    acceptedQuantity: '',
    rejectedQuantity: ''
  });

  // Filters state
  const [filters, setFilters] = useState({
    search: '',
    status: 'all',
    material: 'all',
    inspector: 'all'
  });

  // History filters
  const [historyFilters, setHistoryFilters] = useState({
    days: 7,
    search: ''
  });

  // Fetch QC queue (material-level) with filters
  const { data: qcQueueData, isLoading: inspectionsLoading } = useQuery({
    queryKey: ['qc-material-queue', filters],
    queryFn: async () => {
      const response = await api.get('/warehouse/material-receipts/qc_queue/');
      return response.data;
    },
    refetchInterval: 30000, // Refresh every 30 seconds
    enabled: activeTab === 'pending'
  });

  // Fetch QC history
  const { data: qcHistoryData, isLoading: historyLoading } = useQuery({
    queryKey: ['qc-history', historyFilters],
    queryFn: async () => {
      const response = await api.get('/warehouse/material-receipts/qc_history/', {
        params: {
          days: historyFilters.days,
          limit: 50
        }
      });
      return response.data;
    },
    enabled: activeTab === 'history'
  });

  // Get materials from QC queue or history based on active tab
  const materials = activeTab === 'pending'
    ? (qcQueueData?.materials || [])
    : (qcHistoryData?.materials || []);

  const isLoading = activeTab === 'pending' ? inspectionsLoading : historyLoading;

  // Apply client-side filters
  const inspections = materials.filter(material => {
    // Search filter
    if (filters.search) {
      const search = filters.search.toLowerCase();
      const matchesSearch =
        material.material_code.toLowerCase().includes(search) ||
        material.material_description.toLowerCase().includes(search) ||
        material.truck_number.toLowerCase().includes(search) ||
        material.batch_number?.toLowerCase().includes(search) ||
        material.po_number.toLowerCase().includes(search);
      if (!matchesSearch) return false;
    }

    // Material type filter
    if (filters.material !== 'all' && material.material_type !== filters.material) {
      return false;
    }

    return true;
  });

  // Note: Material types for filter are hardcoded since we derive them from priority
  // Inspectors are not used in material-level QC queue (handled automatically by workflow)

  // Submit inspection mutation (updates MaterialReceipt with QC results)
  const submitInspectionMutation = useMutation({
    mutationFn: async ({ receiptId, data }) => {
      const response = await api.patch(`/warehouse/material-receipts/${receiptId}/`, data);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries(['qc-material-queue']);
      setSelectedInspection(null);
      resetForm();
      success(`Material inspection completed successfully`);
    },
    onError: (err) => {
      showError(err.response?.data?.error || 'Failed to submit inspection');
    }
  });

  // Upload photo mutation
  const uploadPhotoMutation = useMutation({
    mutationFn: async ({ inspectionId, file, caption }) => {
      const formData = new FormData();
      formData.append('photo', file);
      formData.append('caption', caption);

      const response = await api.post(
        `/qc/inspections/${inspectionId}/upload_photo/`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      return response.data;
    },
    onSuccess: () => {
      success('Photo uploaded successfully');
      queryClient.invalidateQueries(['qc-inspections']);
    },
    onError: (err) => {
      showError(err.response?.data?.error || 'Failed to upload photo');
    }
  });

  const handleInspect = (material) => {
    setSelectedInspection(material);
    setInspectionForm({
      visualCheck: '',
      dimensionalCheck: '',
      weightCheck: '',
      packagingCheck: '',
      documentCheck: '',
      notes: '',
      result: '',
      defectTypes: [],
      sampledQuantity: material.pending_inspection || '',
      acceptedQuantity: '',
      rejectedQuantity: ''
    });
  };

  const handleSubmitInspection = (e) => {
    e.preventDefault();

    // Validate all checks are completed
    const allChecksPassed =
      inspectionForm.visualCheck === 'pass' &&
      inspectionForm.dimensionalCheck === 'pass' &&
      inspectionForm.weightCheck === 'pass' &&
      inspectionForm.packagingCheck === 'pass' &&
      inspectionForm.documentCheck === 'pass';

    // Auto-determine result if not explicitly set
    let finalResult = inspectionForm.result;
    if (!finalResult) {
      finalResult = allChecksPassed ? 'PASS' : 'FAIL';
    }

    // Build material receipt update data
    const receiptData = {
      accepted_quantity: parseFloat(inspectionForm.acceptedQuantity) || 0,
      rejected_quantity: parseFloat(inspectionForm.rejectedQuantity) || 0,
      notes: inspectionForm.notes
    };

    submitInspectionMutation.mutate({
      receiptId: selectedInspection.receipt_id,
      data: receiptData
    });
  };

  const resetForm = () => {
    setInspectionForm({
      visualCheck: '',
      dimensionalCheck: '',
      weightCheck: '',
      packagingCheck: '',
      documentCheck: '',
      notes: '',
      result: '',
      defectTypes: [],
      sampledQuantity: '',
      acceptedQuantity: '',
      rejectedQuantity: ''
    });
  };

  // Handle defect type selection (multi-select)
  const handleDefectTypeChange = (e) => {
    const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
    setInspectionForm({ ...inspectionForm, defectTypes: selectedOptions });
  };

  // Handle photo upload
  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (file && selectedInspection) {
      const caption = prompt('Enter photo caption (optional):') || 'Inspection photo';
      uploadPhotoMutation.mutate({
        inspectionId: selectedInspection.id,
        file,
        caption
      });
    }
  };

  // Handle filter changes
  const handleFilterChange = (name, value) => {
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleClearFilters = () => {
    setFilters({
      search: '',
      status: 'all',
      material: 'all',
      inspector: 'all'
    });
  };

  const getStatusBadge = (status) => {
    switch(status) {
      case 'PASS': return 'bg-green-100 text-green-800';
      case 'FAIL': return 'bg-red-100 text-red-800';
      case 'PENDING': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'PASS': return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'FAIL': return <XCircle className="w-5 h-5 text-red-500" />;
      case 'PENDING': return <AlertTriangle className="w-5 h-5 text-yellow-500" />;
      default: return <ClipboardCheck className="w-5 h-5" />;
    }
  };

  const stats = [
    {
      label: 'Materials Pending QC',
      value: inspections.length,
      color: 'bg-yellow-500'
    },
    {
      label: 'At Risk',
      value: inspections.filter(m => m.is_at_risk && !m.is_breached).length,
      color: 'bg-orange-500'
    },
    {
      label: 'SLA Breached',
      value: inspections.filter(m => m.is_breached).length,
      color: 'bg-red-500'
    },
  ];

  // Filter options
  const filterOptions = [
    {
      name: 'material',
      label: 'Material Type',
      value: filters.material,
      options: [
        { value: 'JIT', label: 'JIT' },
        { value: 'Standard', label: 'Standard' },
        { value: 'FOC', label: 'FOC' }
      ]
    }
  ];

  // Defect type options
  const defectTypeOptions = [
    { value: 'dimensional', label: 'Dimensional Issues' },
    { value: 'visual', label: 'Visual Defects' },
    { value: 'weight', label: 'Weight Variance' },
    { value: 'packaging', label: 'Packaging Damage' },
    { value: 'contamination', label: 'Contamination' },
    { value: 'corrosion', label: 'Corrosion/Rust' },
    { value: 'incomplete', label: 'Incomplete Delivery' },
    { value: 'documentation', label: 'Documentation Issues' }
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">QC Inspection</h1>
        <p className="text-gray-600 mt-2">Quality control inspection and material verification</p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('pending')}
            className={`${
              activeTab === 'pending'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors`}
          >
            Pending Inspections
            {qcQueueData?.count > 0 && (
              <span className="ml-2 bg-blue-100 text-blue-600 py-0.5 px-2.5 rounded-full text-xs font-semibold">
                {qcQueueData.count}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`${
              activeTab === 'history'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors`}
          >
            History
            {qcHistoryData?.count > 0 && (
              <span className="ml-2 bg-gray-100 text-gray-600 py-0.5 px-2.5 rounded-full text-xs font-semibold">
                {qcHistoryData.count}
              </span>
            )}
          </button>
        </nav>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {stats.map((stat, index) => (
          <div key={index} className="card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">{stat.label}</p>
                <p className="text-3xl font-bold mt-1">{stat.value}</p>
              </div>
              <div className={`${stat.color} text-white p-3 rounded-lg`}>
                <ClipboardCheck className="w-6 h-6" />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <FilterSection
        filters={filterOptions}
        onFilterChange={handleFilterChange}
        onClearFilters={handleClearFilters}
        searchValue={filters.search}
        searchPlaceholder="Search by batch, truck, material..."
      />

      {/* Inspections Table */}
      <div className="card">
        <h2 className="text-xl font-semibold mb-4">
          {activeTab === 'pending' ? 'Inspection Queue' : 'Inspection History'}
        </h2>

        {isLoading ? (
          <LoadingState message={activeTab === 'pending' ? 'Loading inspections...' : 'Loading history...'} />
        ) : inspections.length === 0 ? (
          <EmptyState
            icon={ClipboardCheck}
            title="No inspections found"
            message={filters.search || filters.status !== 'all'
              ? "Try adjusting your filters"
              : "No inspections pending"}
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-3">Material Code</th>
                  <th className="text-left p-3">Description</th>
                  <th className="text-left p-3">Batch</th>
                  <th className="text-left p-3">Truck / PO</th>
                  <th className="text-left p-3">Quantity</th>
                  <th className="text-left p-3">Priority</th>
                  <th className="text-left p-3">Time in QC</th>
                  <th className="text-left p-3">Status</th>
                  <th className="text-left p-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {inspections.map((material) => (
                  <tr key={material.receipt_id} className={`border-b hover:bg-gray-50 animate-fade-in ${material.is_breached ? 'bg-red-50' : material.is_at_risk ? 'bg-yellow-50' : ''}`}>
                    <td className="p-3 font-semibold text-nexgensis-primary">
                      {material.material_code}
                    </td>
                    <td className="p-3">
                      <div className="flex items-center">
                        <Package className="w-4 h-4 mr-1 text-gray-400" />
                        {material.material_description}
                      </div>
                    </td>
                    <td className="p-3">{material.batch_number || 'N/A'}</td>
                    <td className="p-3">
                      <div className="flex items-center">
                        <TruckIcon className="w-4 h-4 mr-1 text-gray-400" />
                        <div className="text-sm">
                          <div>{material.truck_number}</div>
                          <div className="text-gray-500">{material.po_number}</div>
                        </div>
                      </div>
                    </td>
                    <td className="p-3">
                      <div className="text-sm">
                        <div className="font-medium">{material.pending_inspection} {material.unit_of_measure}</div>
                        <div className="text-gray-500">of {material.received_quantity}</div>
                      </div>
                    </td>
                    <td className="p-3">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        material.material_type === 'JIT' ? 'bg-red-100 text-red-800' :
                        material.material_type === 'FOC' ? 'bg-blue-100 text-blue-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {material.material_type}
                      </span>
                    </td>
                    <td className="p-3">
                      <div className="text-sm">
                        <div className={material.is_breached ? 'text-red-600 font-semibold' : material.is_at_risk ? 'text-yellow-600' : ''}>
                          {Math.floor(material.time_in_qc_minutes / 60)}h {material.time_in_qc_minutes % 60}m
                        </div>
                      </div>
                    </td>
                    <td className="p-3">
                      <div className="flex items-center space-x-2">
                        {material.is_breached ? (
                          <>
                            <XCircle className="w-5 h-5 text-red-500" />
                            <span className="px-3 py-1 rounded text-sm font-medium bg-red-100 text-red-800">
                              BREACHED
                            </span>
                          </>
                        ) : material.is_at_risk ? (
                          <>
                            <AlertTriangle className="w-5 h-5 text-yellow-500" />
                            <span className="px-3 py-1 rounded text-sm font-medium bg-yellow-100 text-yellow-800">
                              AT RISK
                            </span>
                          </>
                        ) : (
                          <>
                            <AlertTriangle className="w-5 h-5 text-yellow-500" />
                            <span className="px-3 py-1 rounded text-sm font-medium bg-yellow-100 text-yellow-800">
                              PENDING
                            </span>
                          </>
                        )}
                      </div>
                    </td>
                    <td className="p-3">
                      {activeTab === 'pending' ? (
                        <button
                          onClick={() => handleInspect(material)}
                          className="btn-primary text-sm"
                        >
                          Inspect
                        </button>
                      ) : (
                        <div className="space-y-1">
                          <div className="flex items-center space-x-2">
                            <CheckCircle className="w-4 h-4 text-green-600" />
                            <span className="text-sm text-green-600 font-medium">
                              {material.accepted_quantity} {material.unit_of_measure}
                            </span>
                          </div>
                          {material.rejected_quantity > 0 && (
                            <div className="flex items-center space-x-2">
                              <XCircle className="w-4 h-4 text-red-600" />
                              <span className="text-sm text-red-600 font-medium">
                                {material.rejected_quantity} {material.unit_of_measure}
                              </span>
                            </div>
                          )}
                          <div className="text-xs text-gray-500">
                            Pass Rate: {material.pass_rate}%
                          </div>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Inspection Modal */}
      {selectedInspection && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto animate-scale-in">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-2xl font-bold">
                  QC Inspection - {selectedInspection.material_code}
                </h2>
                <p className="text-gray-600 mt-1">
                  {selectedInspection.material_description}
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  Batch: {selectedInspection.batch_number || 'N/A'} |
                  Truck: {selectedInspection.truck_number} |
                  PO: {selectedInspection.po_number}
                </p>
              </div>
              <div className="text-right">
                <div className={`px-3 py-1 rounded text-sm font-medium mb-2 ${
                  selectedInspection.material_type === 'JIT' ? 'bg-red-100 text-red-800' :
                  selectedInspection.material_type === 'FOC' ? 'bg-blue-100 text-blue-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {selectedInspection.material_type}
                </div>
                <div className="text-sm text-gray-600">
                  Qty: {selectedInspection.pending_inspection} {selectedInspection.unit_of_measure}
                </div>
              </div>
            </div>

            <form onSubmit={handleSubmitInspection} className="space-y-4">
              {/* QC Checks */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <SmartDropdown
                  label="Visual Check"
                  name="visualCheck"
                  value={inspectionForm.visualCheck}
                  onChange={(e) => setInspectionForm({...inspectionForm, visualCheck: e.target.value})}
                  options={[
                    { value: 'pass', label: '✓ Pass' },
                    { value: 'fail', label: '✗ Fail' }
                  ]}
                  icon={CheckCircle}
                  required
                  placeholder="Select result"
                />

                <SmartDropdown
                  label="Dimensional Check"
                  name="dimensionalCheck"
                  value={inspectionForm.dimensionalCheck}
                  onChange={(e) => setInspectionForm({...inspectionForm, dimensionalCheck: e.target.value})}
                  options={[
                    { value: 'pass', label: '✓ Pass' },
                    { value: 'fail', label: '✗ Fail' }
                  ]}
                  icon={CheckCircle}
                  required
                  placeholder="Select result"
                />

                <SmartDropdown
                  label="Weight Check"
                  name="weightCheck"
                  value={inspectionForm.weightCheck}
                  onChange={(e) => setInspectionForm({...inspectionForm, weightCheck: e.target.value})}
                  options={[
                    { value: 'pass', label: '✓ Pass' },
                    { value: 'fail', label: '✗ Fail' }
                  ]}
                  icon={CheckCircle}
                  required
                  placeholder="Select result"
                />

                <SmartDropdown
                  label="Packaging Check"
                  name="packagingCheck"
                  value={inspectionForm.packagingCheck}
                  onChange={(e) => setInspectionForm({...inspectionForm, packagingCheck: e.target.value})}
                  options={[
                    { value: 'pass', label: '✓ Pass' },
                    { value: 'fail', label: '✗ Fail' }
                  ]}
                  icon={CheckCircle}
                  required
                  placeholder="Select result"
                />

                <SmartDropdown
                  label="Document Check"
                  name="documentCheck"
                  value={inspectionForm.documentCheck}
                  onChange={(e) => setInspectionForm({...inspectionForm, documentCheck: e.target.value})}
                  options={[
                    { value: 'pass', label: '✓ Pass' },
                    { value: 'fail', label: '✗ Fail' }
                  ]}
                  icon={CheckCircle}
                  required
                  placeholder="Select result"
                />

                <SmartDropdown
                  label="Final Result"
                  name="result"
                  value={inspectionForm.result}
                  onChange={(e) => setInspectionForm({...inspectionForm, result: e.target.value})}
                  options={[
                    { value: 'PASS', label: '✓ PASSED' },
                    { value: 'FAIL', label: '✗ FAILED' }
                  ]}
                  icon={ClipboardCheck}
                  required
                  placeholder="Select final result"
                />
              </div>

              {/* Defect Types - Show only when result is FAILED */}
              {inspectionForm.result === 'FAIL' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Defect Types <span className="text-red-500">*</span>
                  </label>
                  <select
                    multiple
                    value={inspectionForm.defectTypes}
                    onChange={handleDefectTypeChange}
                    className="input-field"
                    size="4"
                    required
                  >
                    {defectTypeOptions.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">Hold Ctrl/Cmd to select multiple defect types</p>
                </div>
              )}

              {/* Quantity Fields */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Sampled Quantity
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    name="sampledQuantity"
                    value={inspectionForm.sampledQuantity}
                    onChange={(e) => setInspectionForm({...inspectionForm, sampledQuantity: e.target.value})}
                    className="input-field"
                    placeholder="Sampled qty"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Accepted Quantity <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    name="acceptedQuantity"
                    value={inspectionForm.acceptedQuantity}
                    onChange={(e) => {
                      const acceptedQty = parseFloat(e.target.value) || 0;
                      const sampledQty = parseFloat(inspectionForm.sampledQuantity) || 0;
                      const rejectedQty = Math.max(0, sampledQty - acceptedQty);
                      setInspectionForm({
                        ...inspectionForm,
                        acceptedQuantity: e.target.value,
                        rejectedQuantity: rejectedQty.toFixed(2)
                      });
                    }}
                    className="input-field"
                    placeholder="Accepted qty"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Rejected Quantity <span className="text-gray-500 text-xs">(Auto-calculated)</span>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    name="rejectedQuantity"
                    value={inspectionForm.rejectedQuantity}
                    className="input-field bg-gray-50"
                    placeholder="Auto-calculated"
                    readOnly
                    disabled
                  />
                </div>
              </div>

              {/* Inspector Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Inspector Notes</label>
                <textarea
                  value={inspectionForm.notes}
                  onChange={(e) => setInspectionForm({...inspectionForm, notes: e.target.value})}
                  className="input-field"
                  rows="3"
                  placeholder="Enter inspection notes, observations, or remarks..."
                />
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setSelectedInspection(null);
                    resetForm();
                  }}
                  className="btn-secondary"
                  disabled={submitInspectionMutation.isLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={submitInspectionMutation.isLoading}
                >
                  {submitInspectionMutation.isLoading ? 'Submitting...' : 'Submit Inspection'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default QCInspection;
